import { FC, useState } from 'react';
import Modal from '@new/common/ui/Modal/Modal';
import { ExamQuestionsHeader } from './ExamQuestionHeader';
import { ExamQuestionBody } from './ExamQuestionBody';
import { ExamQuestionsFooter } from './ExamQuestionFooter';
import { useExamContext } from '@new/features/courses/contexts/ExamProvider';
import { getInitialExamQuestionState } from '@new/features/courses/utils/examHelpers';
import type { Quiz } from '@teachur/http-client';

export type UserAnswers = Record<number, number>;

export const ExamQuestionsModal: FC = () => {
  const { examMode, exitExam } = useExamContext();

  if (examMode.type !== 'question') return null;

  return (
    <ExamQuestionsModalContent
      key={examMode.attempt.id}
      quiz={examMode.attempt}
      onClose={exitExam}
    />
  );
};

interface ExamQuestionsModalContentProps {
  quiz: Quiz;
  onClose: () => void;
}

const ExamQuestionsModalContent: FC<ExamQuestionsModalContentProps> = ({ quiz, onClose }) => {
  const [examQuestion, setExamQuestion] = useState(() => getInitialExamQuestionState(quiz));

  const { userAnswers, currentQuestionIndex } = examQuestion;

  const setUserAnswers = (nextUserAnswers: UserAnswers) => {
    setExamQuestion((currentState) => ({
      ...currentState,
      userAnswers: nextUserAnswers,
    }));
  };

  const setCurrentQuestionIndex = (nextQuestionIndex: number) => {
    setExamQuestion((currentState) => ({
      ...currentState,
      currentQuestionIndex: nextQuestionIndex,
    }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={undefined}
      className="h-[90vh] w-4xl"
      dismissable={false}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0">
          <ExamQuestionsHeader quiz={quiz} currentQuestionIndex={currentQuestionIndex} />
        </div>

        <div className="min-h-0 flex-1">
          <ExamQuestionBody
            quiz={quiz}
            currentQuestionIndex={currentQuestionIndex}
            userAnswers={userAnswers}
            setUserAnswers={setUserAnswers}
            setCurrentQuestionIndex={setCurrentQuestionIndex}
          />
        </div>

        <div className="shrink-0">
          <ExamQuestionsFooter
            quiz={quiz}
            userAnswers={userAnswers}
            currentQuestionIndex={currentQuestionIndex}
            setCurrentQuestionIndex={setCurrentQuestionIndex}
          />
        </div>
      </div>
    </Modal>
  );
};
